// Global credits cache to prevent repeated get-credits calls
import { supabase } from '@/integrations/supabase/client';
import { requestThrottler } from './requestThrottle';

interface CreditSummary {
  daily_bonus: number;
  monthly_base: number;
  rollover: number;
  bonus: number;
  stored: number;
  total: number;
  low_credit_warning: boolean;
  current_cycle?: {
    cycle_start_date: string;
    cycle_end_date: string;
    billing_anchor_day: number;
  };
}

interface CacheEntry {
  data: CreditSummary;
  timestamp: number;
  userId: string;
}

class CreditsCache {
  private cache: CacheEntry | null = null;
  private readonly ttl = 5 * 60 * 1000; // 5 minutes TTL

  async getCredits(
    userId: string,
    forceRefresh = false
  ): Promise<CreditSummary> {
    if (!userId) {
      throw new Error('User not authenticated');
    }

    const now = Date.now();

    // Return cached value if still valid and for same user
    if (
      !forceRefresh &&
      this.cache &&
      this.cache.userId === userId &&
      now - this.cache.timestamp < this.ttl
    ) {
      // console.log('[CreditsCache] Using cached credits');
      return this.cache.data;
    }

    // Use request throttler to prevent duplicate calls
    const cacheKey = `credits-${userId}`;

    try {
      // console.log('[CreditsCache] Fetching fresh credits');

      const credits = await requestThrottler.throttle(
        cacheKey,
        async () => {
          const { data, error } = await supabase.functions.invoke(
            'get-credits'
          );

          if (error) throw error;
          return data as CreditSummary;
        },
        { ttl: this.ttl }
      );

      // Update cache
      this.cache = {
        data: credits,
        timestamp: now,
        userId,
      };

      return credits;
    } catch (error) {
      console.error('[CreditsCache] Failed to fetch credits:', error);

      // Return cached data if available as fallback
      if (this.cache && this.cache.userId === userId) {
        console.log('[CreditsCache] Returning stale cache due to error');
        return this.cache.data;
      }

      throw error;
    }
  }

  // Clear cache (useful after credit adjustments)
  clear() {
    console.log('[CreditsCache] Clearing credits cache');
    this.cache = null;
    requestThrottler.clearPattern('credits-');
  }

  // Clear cache for specific user
  clearUser(userId: string) {
    if (this.cache && this.cache.userId === userId) {
      console.log(`[CreditsCache] Clearing cache for user: ${userId}`);
      this.cache = null;
    }
    requestThrottler.clearKey(`credits-${userId}`);
  }

  // Get cache stats for debugging
  getStats() {
    if (!this.cache) {
      return { cached: false, age: 0 };
    }

    const age = Date.now() - this.cache.timestamp;
    const isValid = age < this.ttl;

    return {
      cached: true,
      age: Math.round(age / 1000), // seconds
      isValid,
      userId: this.cache.userId,
    };
  }
}

export const creditsCache = new CreditsCache();
